import { useEffect, useState } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { ReadyToSharePosts } from 'src/app/shared/api/readyToShare';

import { useFetchReadyToSharePost } from '../useFetchReadyToSharePost';

export function useHandlePolling() {
	const {
		data: {
			petition: { slug },
			viewer: {
				experiments: { starterExperienceDashboardReadyToShareV2 },
			},
			readyToSharePosts,
		},
	} = useStarterDashboardV2PageData();
	const isVariation = starterExperienceDashboardReadyToShareV2.variation === 'variant1';
	/**
	 * Case 1: variation is control -> value is false
	 * Case 2: variation is variant1 but readyToSharePosts is undefined -> value is false
	 * Case 3: variation is variant1 and readyToSharePosts is defined -> value is true
	 */
	const [shouldShowReadyToShareExperiment, setShouldShowReadyToShareExperiment] = useState<boolean>(
		isVariation && !!readyToSharePosts,
	);
	const [isLoading, setLoading] = useState<boolean>(isVariation && !!readyToSharePosts);
	const [readyToSharePostsValue, setReadyToSharePostsValue] = useState<ReadyToSharePosts | undefined>(undefined);

	const readyToSharePostsQueryState = useFetchReadyToSharePost({
		slug,
		shouldExecute: shouldShowReadyToShareExperiment,
	});

	useEffect(() => {
		/**
		 * Stop loading if data is not loaded because of error and
		 * `shouldShowReadyToShareExperiment` is set to false
		 */
		if (readyToSharePostsQueryState.isError) {
			setLoading(false);
			setShouldShowReadyToShareExperiment(false);
			return;
		}

		if (readyToSharePostsQueryState.isSuccess) {
			/**
			 * Data was loaded:
			 * - Set the new data
			 * - Stop loading
			 */
			setReadyToSharePostsValue(readyToSharePostsQueryState.data);
			setLoading(false);
		}
	}, [readyToSharePostsQueryState.isError, readyToSharePostsQueryState.isSuccess, readyToSharePostsQueryState.data]);

	return {
		isLoading,
		readyToSharePostsValue,
		shouldShowReadyToShareExperiment,
	};
}
